import dayjs from 'dayjs';
import { equals } from 'rambda';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'rxjs';
import { useDebounceCallback, useEventListener } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';
import Flyout from '../Flyout';

type Item = {
  key: string;
  label: string;
  value: string;
};

type Props = {
  show: boolean;
  onCancel: () => void;
  onConfirm: (value: string) => void;
  title?: string;
  items: Item[];
  defaultValue?: string;
};

const HEIGHT = 224; // py-24 + h-8 = 192

export default function ScrollSelector({ show, onCancel, onConfirm, title, items, defaultValue }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<string | null>(defaultValue ?? null);

  // The container is not ready when the component is mounted
  const debounceAddListener = useDebounceCallback(
    () => {
      const container = containerRef.current;
      if (!container) return;

      const io = new IntersectionObserver(
        (entries) => {
          const value = entries.filter((x) => x.isIntersecting)[0]?.target?.getAttribute('data-value');
          if (value) {
            setSelected(value);
          }
        },
        {
          root: container,
          rootMargin: `-${(HEIGHT - 32) / 2}px 0px -${(HEIGHT - 32) / 2}px 0px`,
          threshold: 0.5,
        },
      );

      for (const el of container.querySelectorAll('div[data-value]')) {
        io.observe(el);
      }

      return () => {
        io.disconnect();
      };
    },
    300,
    { leading: false, trailing: true },
  );

  useLayoutEffect(debounceAddListener, [show]);

  const debounceScrollToDefault = useDebounceCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const el = container.querySelector(`div[data-value="${defaultValue}"]`);
    if (el) {
      el.scrollIntoView({ block: 'center' });
    }
  }, 100);

  useLayoutEffect(() => {
    if (show) {
      debounceScrollToDefault();
    }
  }, [show, defaultValue]);

  const handleConfirm = () => {
    if (selected) {
      onConfirm(selected);
      dayjs.locale(selected);
    }
  };
  useEventListener('keydown', (e) => {
    if (!show) return;
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown': {
        return setSelected((selected) => {
          const prevIndex = items.findIndex((item) => item.value === selected);
          const newIndex = prevIndex + (e.key === 'ArrowUp' ? -1 : 1);
          if (newIndex < 0 || newIndex >= items.length) return selected;

          listRef.current?.children[newIndex]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return items[newIndex]?.value ?? null;
        });
      }
      case 'Enter': {
        return handleConfirm();
      }
      case 'Escape': {
        return onCancel();
      }
    }
  });

  return (
    <Flyout
      show={show}
      onClose={noop}
      className='m-0 !pb-0'>
      <div className='inset-x-0 flex flex-col bg-neutral-0 pb-10 pt-1'>
        <div className='flex items-center justify-between px-2'>
          <button
            onClick={onCancel}
            className='btn btn-ghost btn-sm'>
            {t('common.cancel')}
          </button>

          <h2 className='text-secondary'>{title}</h2>

          <button
            className='btn btn-ghost btn-sm'
            onClick={handleConfirm}>
            {t('common.confirm')}
          </button>
        </div>

        <div
          className='no-scrollbar overflow-auto'
          style={{
            maskImage: 'linear-gradient(transparent, black, transparent)',
            height: `${HEIGHT}px`,
            maxHeight: `${HEIGHT}px`,
            minHeight: `${HEIGHT}px`,
          }}
          ref={containerRef}>
          <div
            className='mx-1 flex flex-col py-24 select-none'
            ref={listRef}>
            {items.map((item) => (
              <div
                key={item.key}
                data-value={item.value}
                data-selected={equals(item.value, selected)}
                onClick={(e) => {
                  setSelected(item.value);
                  (e.target as HTMLDivElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
                }}
                className={cn(
                  'flex h-8 items-center justify-center rounded-md text-secondary',
                  equals(item.value, selected) && 'bg-base-100 text-body',
                )}>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Flyout>
  );
}
